import { SortIcon } from "@bookingcom/bui-assets-react/streamline";
import { Chip, DropdownMenu, Stack, Text } from "@bookingcom/bui-react";
import { SBSortingOrder } from "@bookingcom/flights-searchbox";
import { TranslationMessage } from "@bookingcom/lingojs-core/build/t";
import { t, useI18n } from "@bookingcom/lingojs-react";
import useCreateUrl from "hooks/useCreateUrl";
import useEventTracking from "hooks/useEventTracking";
import useGlobalContext from "hooks/useGlobalContext";
import React, { useCallback, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";

type Sort = Extract<SBSortingOrder, "BEST" | "CHEAPEST" | "FASTEST">;

const sortLabels: Record<Sort, { title: TranslationMessage<any, any>; subtitle?: TranslationMessage<any, any> }> = {
  BEST: { title: t("flights_search_sort_best"), subtitle: t("flights_search_sort_best_tooltip") },
  CHEAPEST: { title: t("flights_search_sort_cheapest") },
  FASTEST: { title: t("flights_search_sort_fastest") }
};

const dropdownAttributes = { style: { width: 360 } };

const SearchSortSelector = () => {
  const i18n = useI18n();
  const history = useHistory();
  const { enhanceURLSearchParams } = useCreateUrl();
  const { requestId } = useGlobalContext();

  const trackV2 = useEventTracking("search_results", requestId);

  const [sorting, setSorting] = useState<Sort>(() => {
    const query = new URLSearchParams(location.search);
    return (query.get("sort") as Sort) || "BEST";
  });

  const sortName = i18n.trans(sortLabels[sorting].title);

  const handleItemChoose = useCallback(
    (sort: Sort) => {
      setSorting(sort);

      setTimeout(() => {
        const params = new URLSearchParams(location.search);
        params.set("sort", sort);
        params.delete("page");

        enhanceURLSearchParams(params);
        history.replace(
          {
            pathname: location.pathname,
            search: params.toString()
          },
          { hideLoader: true }
        );
      });
      trackV2("click_sort_by_link", sort);
    },
    [enhanceURLSearchParams, history, trackV2]
  );

  const items = useMemo(
    () =>
      Object.entries(sortLabels).map(([sort, { title, subtitle }]) => ({
        text: (
          <Stack gap={0}>
            <Text>{i18n.trans(title)}</Text>
            {subtitle && (
              <Text variant="small_1" color={"neutral_alt"}>
                {i18n.trans(subtitle)}
              </Text>
            )}
          </Stack>
        ),
        onChoose: () => handleItemChoose(sort as Sort)
      })),
    [handleItemChoose, i18n]
  );

  return (
    <DropdownMenu position="bottom-end" items={items} attributes={dropdownAttributes}>
      {(attributes) => (
        <Chip
          variant="action"
          label={i18n.trans(t("flights_sort_by_name", { variables: { sort_name: sortName } }))}
          checked={true}
          icon={<SortIcon />}
          attributes={attributes}
        />
      )}
    </DropdownMenu>
  );
};

export default SearchSortSelector;
